import {
  IBOMParameterIncluded,
  IBOMSetupOnboardPostBody,
  IBOMTaxBody,
  IBOMVialType,
  IInventoryPurchaseOrderDraftProductInfo,
  IInventoryPurchaseOrderProductSearchResponseData,
} from "@/api_framework/api_modals/Inventory";
import Input from "@/app/components/ui/Inputs/Input";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import { useCouponCategoryAPI } from "@/app/hooks/api_hooks/coupon/useCouponCategoryAPI";
import { useCouponSubCategoryAPI } from "@/app/hooks/api_hooks/coupon/useCouponSubCategoryAPI";
import { useBOMSetupOnboardAPI } from "@/app/hooks/api_hooks/Inventory/BOMSetup/useBOMSetupOnboardAPI";
import { useInventoryActiveTaxListAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryActiveTaxListAPI";
import useFormValidations from "@/app/hooks/useFormValidations";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import { TrashIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";
import InventoryProductSearchComponent from "../../OrderPurchaseComponent/OrderPurchaseAddComponent/InventoryProductSearchComponent";
import { useSearchParams } from "react-router-dom";
import { useBOMSetupUpdateAPI } from "@/app/hooks/api_hooks/Inventory/BOMSetup/useBOMSetupUpdateAPI";
import { useBOMSetupCityListAPI } from "@/app/hooks/api_hooks/Inventory/BOMSetup/useBOMSetupCityListAPI";
import { useBOMSetupDetailAPI } from "@/app/hooks/api_hooks/Inventory/BOMSetup/useBOMSetupDetailAPI";
import { useInventoryProductCatalougeDropdownAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryProductCatalougeDropdownAPI";
import ToggleWithIcon from "@/app/components/ui/Toggles/ToggleWithIcon";
import { useBOMVialTypeAPI } from "@/app/hooks/api_hooks/Inventory/BOMSetup/useBOMVialTypeAPI";
import { useBOMVaccineFamilyDoseAPI } from "@/app/hooks/api_hooks/Inventory/BOMSetup/useBOMVaccineFamilyDoseAPI";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useBOMTaxOnboardAPI } from "@/app/hooks/api_hooks/Inventory/BOMSetup/useBOMTaxOnboardAPI";
import { useBOMSampleTypeAPI } from "@/app/hooks/api_hooks/Inventory/BOMSetup/useBOMSampleTypeAPI";
import { useBOMLisParametersAPI } from "@/app/hooks/api_hooks/Inventory/BOMSetup/useBOMLisParametersAPI";
import RBACFlag from "@/app/components/core/RBACFlag";
import { useUtils } from "@/app/hooks/useUtils";

export interface IBOMFormGeneralFields {
  bomType: string;
  bomTypeId: string;
  bom_name: string;
  pet_species: string;
  petAgeStartValue: string;
  petAgeEndValue: string;
  petWeightStartValue: string;
  petWeightEndValue: string;
  vmscategory: string;
  vmscategoryId: string;
  vmsSubcategory: string;
  vmsSubcategoryId: string;
  comments: string;
  invoice: string;
  sampleType: string;
  vialType: IBOMVialType[];
  sampleQuantity: string;
  parametersIncluded: IBOMParameterIncluded[];
  familyVaccine: string;
  familyVaccineId?: string;
  dose: string;
  doseId?: string;
  vmsVisible: boolean;
  requisities: string;
  hsncode: string;
  cgst: number;
  sgst: number;
  igst: number;
  ugst: number;
  qty: number;
  products: {
    sku_name: string;
    sku_id: string;
    variant_id?: string;
    qty_per_pkg: number;
    qty: string;
  }[];
  POProductsHook: IInventoryPurchaseOrderDraftProductInfo[];
  certificate_type: string;
  certificate_id: string;
}

const bomType = [
  {
    id: "1",
    name: "Lab",
  },
  {
    id: "2",
    name: "Vaccine",
  },
  {
    id: "3",
    name: "Treatment",
  },
  {
    id: "4",
    name: "Grooming",
  },
];

const BomGeneralDetails = () => {
  const { getRouteKey } = useRouter();
  const [params, _setParams] = useSearchParams();
  const { forOnlyNumbers, forOnlyNumber, forOnlyNumbersWithDecimal } = useFormValidations();
  const navigate = useNavigate();
  const { register } = useForm();
  const routeId = getRouteKey("INVENTORY", "id");
  const { routeState } = useRouteInfo(routeId);
  const { taxList, bomVialType, bomVaccineFamilyDose, bomSampleType, bomLisParameters } =
    routeState.state as IInventoryState;
  const fetchTaxList = useInventoryActiveTaxListAPI();
  const { execute: fetchCategory, serviceCategory: couponCategory } = useCouponCategoryAPI();
  const { execute: fetchSubCategory, serviceSubCategory: couponSubCategory } = useCouponSubCategoryAPI();
  const { execute: createBOM } = useBOMSetupOnboardAPI();
  const bom_id = params.get("bom_id");
  const { execute: updateBOM } = useBOMSetupUpdateAPI();
  const { execute: fetchBomCityList } = useBOMSetupCityListAPI();
  const { execute: fetchBomDetail, bomDetail } = useBOMSetupDetailAPI();
  const taxListItems = useSelectMenuReducer(taxList, "hsn_code", "hsn_code");
  const couponCategoryItem = useSelectMenuReducer(couponCategory, "category_name", "id");
  const couponSubCategoryItem = useSelectMenuReducer(couponSubCategory, "subcategory_name", "subcategory_id");
  const bomTypeItems = useSelectMenuReducer(bomType, "name", "id");
  const { searchResults: animalItems, execute: fetchAnimals } = useInventoryProductCatalougeDropdownAPI("ANIMAL_TYPE");
  const animalItemsList = useSelectMenuReducer(animalItems, "animal_type", "animal_type_id");
  const bomLisParametersItems = useSelectMenuReducer(
    (bomLisParameters || []).filter((param) => Boolean(param?.parameter_name)),
    "parameter_name",
    "parameter_id",
  );
  const fetchBomVialType = useBOMVialTypeAPI();
  const bomVialTypeItems = useSelectMenuReducer(bomVialType, "vial_type", "vial_id");
  const fetchVaccineFamilyDose = useBOMVaccineFamilyDoseAPI();
  const bomVaccineFamilyDoseItems = useSelectMenuReducer(bomVaccineFamilyDose, "vaccine_family_name", "vaccine_id");
  const { DOCUMENT_TYPE_OPTIONS } = useUtils();
  const certificates = DOCUMENT_TYPE_OPTIONS;

  const { execute: updateBomTax } = useBOMTaxOnboardAPI();
  const fetchBOMSampleType = useBOMSampleTypeAPI();
  const bomSampleTypeItems = useSelectMenuReducer(bomSampleType, "sample_type", "sample_id");
  const fetchBomLisParameters = useBOMLisParametersAPI();

  const formHook = useForm<IBOMFormGeneralFields>({
    mode: "onChange",
    defaultValues: {
      products: [],
    },
  });

  const selectedFamilyId = formHook.watch("familyVaccineId");
  const selectedFamilyName = formHook.watch("familyVaccine");
  const selectedVaccineFamily = useMemo(
    () =>
      bomVaccineFamilyDose?.find(
        (family) => family.vaccine_id === selectedFamilyId || family.vaccine_family_name === selectedFamilyName,
      ),
    [bomVaccineFamilyDose, selectedFamilyId, selectedFamilyName],
  );
  const doseItems = useMemo(
    () => (selectedVaccineFamily?.dose || []).map((d) => ({ id: d.dose_id, title: d.dose_name })),
    [selectedVaccineFamily],
  );
  const selectedBomType = formHook.watch("bomType");
  const isLabBomType = selectedBomType === "Lab";
  const isBomType = selectedBomType === "Vaccine";

  const handleAddProductItem = (item: IInventoryPurchaseOrderProductSearchResponseData) => {
    const prevProducts = formHook.watch("products");
    const isPresent = prevProducts.some((f) => f.variant_id === item.vetic_variant_id);
    if (isPresent) {
      toast.error("Product already added.");
      return;
    }
    formHook.setValue("products", [
      ...prevProducts,
      {
        sku_name: item?.variant_name,
        sku_id: item?.vetic_variant_id,
        variant_id: item.vetic_variant_id,
        qty_per_pkg: item.qty_per_package,
        qty: null,
      },
    ]);
  };

  const getGeneralValidationFields = () => {
    const fields: string[] = [
      "bomType",
      "bom_name",
      "pet_species",
      "petAgeStartValue",
      "petAgeEndValue",
      "petWeightStartValue",
      "petWeightEndValue",
      "vmscategory",
      "vmsSubcategory",
      "requisities",
      "certificate_type",
    ];

    if (isLabBomType) {
      fields.push("vialType");
    }

    if (isBomType) {
      fields.push("familyVaccine", "dose");
    }

    const productQtyFields = formHook.getValues("products")?.map((_, index) => `products.${index}.qty`) ?? [];

    return [...fields, ...productQtyFields];
  };

  const handleGeneralSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const fieldsToValidate = getGeneralValidationFields();
    const isValid = await formHook.trigger(fieldsToValidate as any);
    const data = formHook.getValues();
    const products = formHook.watch("products");
    if (products?.length === 0) {
      toast.error("Please select at least one product");
      return;
    }
    const invalidProduct = products?.some((product) => product.qty === "0");
    if (invalidProduct) {
      toast.error("Product Quantity should be greater than 0");
      return;
    }
    if (!isValid) return;
    if (bom_id) fetchBomCityList();
    const shouldSendLabDetails = data?.bomType === "Lab";
    const shouldSendVaccinationDetails = data?.bomType === "Vaccine";
    const generalDetails: IBOMSetupOnboardPostBody["general_details"] = {
      service_details: {
        bom_type: data?.bomType,
        bom_name: data?.bom_name,
      },
      product_component: data.products.map((p) => {
        return { sku_name: p.sku_name, sku_id: p.sku_id, qty_per_pkg: parseInt(`${p.qty_per_pkg}`), qty: p.qty };
      }),
      pet_details: {
        pet_species: data?.pet_species,
        pet_age_range: {
          pet_age_min: parseFloat(data?.petAgeStartValue),
          pet_age_max: parseFloat(data?.petAgeEndValue),
        },
        pet_weight_range: {
          pet_weight_min: parseFloat(data?.petWeightStartValue),
          pet_weight_max: parseFloat(data?.petWeightEndValue),
        },
      },
      pre_requisites: data?.requisities,
      document_type: data?.certificate_type,
      comments: data?.comments,
      invoice: data?.invoice,
      ...(shouldSendLabDetails
        ? {
            lab_details: {
              sample_type: data?.sampleType,
              vial_type: data?.vialType ?? [],
              sample_quantity: data?.sampleQuantity,
              parameters_included: (data?.parametersIncluded || []).map((param) => ({
                id: param?.id || "",
                name: param?.name,
                method_id: param?.method_id ?? null,
              })),
            },
          }
        : {}),
      ...(shouldSendVaccinationDetails
        ? {
            vaccination_details: {
              vaccine_name: data?.familyVaccine,
              vaccine_id: data?.familyVaccineId,
              dose_name: data?.dose,
              dose_id: data?.doseId,
            },
          }
        : {}),
    };
    const constructedBody: IBOMSetupOnboardPostBody = {
      vms_category_id: data?.vmscategoryId,
      vms_category_name: data?.vmscategory,
      vms_subcategory_name: data?.vmsSubcategory,
      vms_subcategory_id: data?.vmsSubcategoryId,
      status: data?.vmsVisible ? true : false,
      general_details: generalDetails,
    };
    if (bom_id) {
      const { status } = await updateBOM(constructedBody, bom_id);
      if (status) {
        navigate(`/app/inventory/bom-onboard?page_step=clinic_price&&bom_id=${bom_id}`);
      }
    } else {
      const { status, message } = await createBOM(constructedBody);
      if (status) {
        navigate(`/app/inventory/bom-onboard?page_step=clinic_price&&bom_id=${message}`);
      }
    }
  };

  useEffect(() => {
    fetchCategory();
    const formData = formHook.getValues();
    if (formData.vmscategoryId && formData.vmscategoryId !== "0") {
      fetchSubCategory(formData.vmscategoryId);
    }
  }, [formHook.watch("vmscategoryId")]);

  useEffect(() => {
    fetchTaxList();
    if (bom_id) {
      fetchBomDetail(bom_id);
    }
  }, [bom_id]);

  useEffect(() => {
    const hasBomDetail = bomDetail && typeof bomDetail === "object" && Object.keys(bomDetail).length > 0;
    if (hasBomDetail) {
      formHook.setValue("vmscategoryId", bomDetail?.vms_category_id);
      formHook.setValue("vmscategory", bomDetail?.vms_category_name);
      formHook.setValue("vmsSubcategoryId", bomDetail?.vms_subcategory_id);
      formHook.setValue("vmsSubcategory", bomDetail?.vms_subcategory_name);
      formHook.setValue("pet_species", bomDetail?.general_details?.pet_details?.pet_species);
      formHook.setValue("petAgeStartValue", bomDetail?.general_details?.pet_details?.pet_age_range?.pet_age_min);
      formHook.setValue("petAgeEndValue", bomDetail?.general_details?.pet_details?.pet_age_range?.pet_age_max);
      formHook.setValue(
        "petWeightStartValue",
        bomDetail?.general_details?.pet_details?.pet_weight_range?.pet_weight_min,
      );
      formHook.setValue("petWeightEndValue", bomDetail?.general_details?.pet_details?.pet_weight_range?.pet_weight_max);
      formHook.setValue("products", bomDetail?.general_details?.product_component);
      formHook.setValue("bomType", bomDetail?.general_details?.service_details?.bom_type);
      formHook.setValue("bom_name", bomDetail?.general_details?.service_details?.bom_name);
      formHook.setValue("sampleType", bomDetail?.general_details?.lab_details?.sample_type);
      const vialTypeFromApi = bomDetail?.general_details?.lab_details?.vial_type;
      formHook.setValue("vialType", Array.isArray(vialTypeFromApi) ? vialTypeFromApi : []);
      formHook.setValue("sampleQuantity", bomDetail?.general_details?.lab_details?.sample_quantity);
      formHook.setValue(
        "parametersIncluded",
        (bomDetail?.general_details?.lab_details?.parameters_included || []).map((p: any) => {
          const name = typeof p === "string" ? p : (p?.name ?? p?.parameter_name);
          const matchedParameter = (bomLisParameters || []).find(
            (param) =>
              param?.parameter_id === (typeof p === "string" ? undefined : (p?.id ?? p?.parameter_id)) ||
              param?.parameter_name === name,
          );
          return {
            id:
              (typeof p === "string" ? undefined : (p?.id ?? p?.parameter_id)) || matchedParameter?.parameter_id || "",
            name,
            method_id: (typeof p === "string" ? null : p?.method_id) ?? matchedParameter?.method_id ?? null,
          };
        }),
      );
      formHook.setValue(
        "familyVaccine",
        bomDetail?.general_details?.vaccination_details?.vaccine_name ||
          bomDetail?.general_details?.vaccination_details?.family_of_vaccine,
      );
      formHook.setValue(
        "dose",
        bomDetail?.general_details?.vaccination_details?.dose_name ||
          bomDetail?.general_details?.vaccination_details?.dose,
      );
      formHook.setValue("comments", bomDetail?.general_details?.comments);
      formHook.setValue("requisities", bomDetail?.general_details?.pre_requisites);
      formHook.setValue("certificate_type", bomDetail?.general_details?.document_type);
      formHook.setValue("invoice", bomDetail?.general_details?.invoice);
      formHook.setValue("vmsVisible", bomDetail?.status);
      formHook.setValue("hsncode", bomDetail?.general_details?.tax_details?.hsn_code);
      formHook.setValue("cgst", bomDetail?.general_details?.tax_details?.tax_rate?.cgst);
      formHook.setValue("igst", bomDetail?.general_details?.tax_details?.tax_rate?.igst);
      formHook.setValue("sgst", bomDetail?.general_details?.tax_details?.tax_rate?.sgst);
      formHook.setValue("ugst", bomDetail?.general_details?.tax_details?.tax_rate?.utgst);
    }
  }, [bomDetail]);

  const handleCancel = () => {
    formHook?.reset();
    navigate(getRouteKey("BOM_SETUP", "url"));
  };

  useEffect(() => {
    fetchAnimals();
    fetchBomVialType();
    fetchBOMSampleType();
    fetchBomLisParameters();
  }, []);

  useEffect(() => {
    if (!bomDetail || !bomVaccineFamilyDose?.length) return;
    const familyName =
      bomDetail?.general_details?.vaccination_details?.vaccine_name ||
      bomDetail?.general_details?.vaccination_details?.family_of_vaccine;
    const doseName =
      bomDetail?.general_details?.vaccination_details?.dose_name ||
      bomDetail?.general_details?.vaccination_details?.dose;
    if (!familyName) return;
    const family = bomVaccineFamilyDose.find((f) => f.vaccine_family_name === familyName);
    if (family) {
      formHook.setValue("familyVaccineId", family.vaccine_id);
      const doseObj = family.dose.find((d) => d.dose_name === doseName);
      if (doseObj) formHook.setValue("doseId", doseObj.dose_id);
    }
  }, [bomDetail, bomVaccineFamilyDose]);

  const petSpecies = formHook.watch("pet_species");

  useEffect(() => {
    if (petSpecies) {
      fetchVaccineFamilyDose(petSpecies);
    }
  }, [petSpecies]);

  const parseOptionalNumber = (val: unknown): number | null => {
    if (val === undefined || val === null || val === "") return null;
    const parsed = typeof val === "number" ? val : Number(val);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const handleTaxOnboard = async (data: IBOMFormGeneralFields) => {
    const constructedTaxBody: IBOMTaxBody = {
      tax_details: {
        hsn_code: data?.hsncode,
        tax_rate: {
          cgst: data?.cgst,
          igst: data?.igst,
          sgst: data?.sgst,
          utgst: data?.ugst,
        },
      },
    };

    if (bom_id) {
      const { status } = await updateBomTax(constructedTaxBody, bom_id);
      if (status) {
        navigate(`/app/inventory/bom-setup`);
      }
    }
  };

  return (
    <div className="relative">
      <div className="border-gray-200 border-b">
        <h3 className="font-semibold text-base text-gray-900 leading-6 py-2">BOM Setup</h3>
      </div>

      <RBACFlag actionId="BOM_GENERAL_DETAILS">
        <form className="bg-white py-4 rounded-md w-full" onSubmit={handleGeneralSubmit}>
          <p className="text-md font-semibold">General Details</p>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
            <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
              <span className="font-normal text-black text-xs">BOM Type</span>
              <span className="font-normal text-rose-600 text-xs">*</span>
            </div>
            <SearchableSelectMenu
              errorMessages={[{ message: "BOM Type is required", type: "required" }]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("bomType", item.title);
                  formHook.setValue("bomTypeId", item.id);
                }
              }}
              fieldError={formHook.formState.errors.bomType}
              register={formHook.register("bomType", { required: true })}
              selectItems={bomTypeItems}
              placeholder="e.g. Select BOM Type"
              showTooltips={true}
              showTypedErrors={true}
              showDropdownIcon={true}
              withDropDownSearch
              defaultSelected={bomTypeItems?.filter((oc) => oc.title === formHook.watch("bomType"))[0]}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full sm:w-[300px]"
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
            <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
              <span className="font-normal text-black text-xs">BOM Name</span>
              <span className="font-normal text-rose-600 text-xs">*</span>
            </div>
            <Input
              register={formHook?.register("bom_name", {
                required: true,
                validate: (value) => value.trim() !== "" || "Name cannot be empty or only spaces",
              })}
              type="text"
              fieldError={formHook?.formState?.errors?.bom_name}
              errorMessages={[
                { message: "BOM Name is required", type: "required" },
                { message: "Name cannot be empty or only spaces", type: "validate" },
              ]}
              className="border-slate-400 rounded-md w-full sm:w-[300px] h-9 text-sm"
              inputClassName="h-[38px] rounded-md"
              placeholder="e.g. Dog"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                formHook?.setValue("bom_name", e.target.value);
                formHook.clearErrors("bom_name");
              }}
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
            <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
              <span className="font-normal text-black text-xs">Pet Species</span>
              <span className="font-normal text-rose-600 text-xs">*</span>
            </div>
            <SearchableSelectMenu
              errorMessages={[{ message: "Pet Species is required", type: "required" }]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("pet_species", item?.title);
                }
              }}
              fieldError={formHook.formState.errors.pet_species}
              register={formHook.register("pet_species", { required: true })}
              selectItems={animalItemsList}
              placeholder="e.g. Canine"
              showTooltips={true}
              showTypedErrors={true}
              showDropdownIcon={true}
              withDropDownSearch
              defaultSelected={animalItemsList?.filter((oc) => oc?.title === formHook.watch("pet_species"))[0]}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full sm:w-[300px]"
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
            <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
              <span className="font-normal text-black text-xs">Pet Age Range</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Input
                className="border-slate-400 rounded-md w-full sm:w-[145px] h-9 text-sm"
                inputClassName="h-[38px] rounded-md"
                placeholder="Start Value"
                register={formHook.register("petAgeStartValue", {
                  required: false,
                  ...forOnlyNumber.validations,
                  validate: {
                    nonNegative: (value: unknown) => {
                      const parsedValue = parseOptionalNumber(value);
                      return parsedValue === null || parsedValue >= 0;
                    },
                    startGreaterThanEnd: (value: unknown) => {
                      const parsedStart = parseOptionalNumber(value);
                      const parsedEnd = parseOptionalNumber(formHook.getValues("petAgeEndValue"));
                      if (parsedStart === null || parsedEnd === null) {
                        return true;
                      }
                      return parsedStart <= parsedEnd;
                    },
                  },
                })}
                fieldError={formHook.formState.errors?.petAgeStartValue}
                errorMessages={[
                  forOnlyNumber.errors,
                  { message: "Start value cannot be negative", type: "nonNegative" },
                  { message: "Start value cannot be greater than end value", type: "startGreaterThanEnd" },
                ]}
              />
              <Input
                className="border-slate-400 rounded-md w-full sm:w-[145px] h-9 text-sm"
                inputClassName="h-[38px] rounded-md"
                placeholder="End Value"
                register={formHook.register("petAgeEndValue", {
                  required: false,
                  ...forOnlyNumber.validations,
                  validate: {
                    nonNegative: (value: unknown) => {
                      const parsedValue = parseOptionalNumber(value);
                      return parsedValue === null || parsedValue >= 0;
                    },
                    endLessThanStart: (value: unknown) => {
                      const parsedEnd = parseOptionalNumber(value);
                      const parsedStart = parseOptionalNumber(formHook.getValues("petAgeStartValue"));
                      if (parsedStart === null || parsedEnd === null) {
                        return true;
                      }
                      return parsedEnd >= parsedStart;
                    },
                  },
                })}
                fieldError={formHook.formState.errors?.petAgeEndValue}
                errorMessages={[
                  forOnlyNumber.errors,
                  { message: "End value cannot be negative", type: "nonNegative" },
                  { message: "End value must be greater than or equal to start value", type: "endLessThanStart" },
                ]}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
            <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
              <span className="font-normal text-black text-xs">Pet Weight Range (in KG)</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Input
                className="border-slate-400 rounded-md w-full sm:w-[145px] h-9 text-sm"
                inputClassName="h-[38px] rounded-md"
                placeholder="Start Value"
                register={formHook.register("petWeightStartValue", {
                  required: false,
                  ...forOnlyNumber.validations,
                  validate: {
                    nonNegative: (value: unknown) => {
                      const parsedValue = parseOptionalNumber(value);
                      return parsedValue === null || parsedValue >= 0;
                    },
                    startGreaterThanEnd: (value: unknown) => {
                      const parsedStart = parseOptionalNumber(value);
                      const parsedEnd = parseOptionalNumber(formHook.getValues("petWeightEndValue"));
                      if (parsedStart === null || parsedEnd === null) {
                        return true;
                      }
                      return parsedStart <= parsedEnd;
                    },
                  },
                })}
                fieldError={formHook.formState.errors?.petWeightStartValue}
                errorMessages={[
                  forOnlyNumber.errors,
                  { message: "Start value cannot be negative", type: "nonNegative" },
                  { message: "Start value cannot be greater than end value", type: "startGreaterThanEnd" },
                ]}
              />
              <Input
                className="border-slate-400 rounded-md w-full sm:w-[145px] h-9 text-sm"
                inputClassName="h-[38px] rounded-md"
                placeholder="End Value"
                register={formHook.register("petWeightEndValue", {
                  required: false,
                  ...forOnlyNumber.validations,
                  validate: {
                    nonNegative: (value: unknown) => {
                      const parsedValue = parseOptionalNumber(value);
                      return parsedValue === null || parsedValue >= 0;
                    },
                    endLessThanStart: (value: unknown) => {
                      const parsedEnd = parseOptionalNumber(value);
                      const parsedStart = parseOptionalNumber(formHook.getValues("petWeightStartValue"));
                      if (parsedStart === null || parsedEnd === null) {
                        return true;
                      }
                      return parsedEnd >= parsedStart;
                    },
                  },
                })}
                fieldError={formHook.formState.errors?.petWeightEndValue}
                errorMessages={[
                  forOnlyNumber.errors,
                  { message: "End value cannot be negative", type: "nonNegative" },
                  { message: "End value must be greater than or equal to start value", type: "endLessThanStart" },
                ]}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
            <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
              <span className="font-normal text-black text-xs">Category</span>
              <span className="font-normal text-rose-600 text-xs">*</span>
            </div>
            <SearchableSelectMenu
              errorMessages={[{ message: "Category is required", type: "required" }]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("vmscategory", item.title);
                  formHook.setValue("vmscategoryId", item.id);
                }
              }}
              fieldError={formHook.formState.errors.vmscategory}
              register={formHook.register("vmscategory", { required: true })}
              selectItems={couponCategoryItem}
              placeholder="Select Category"
              showTooltips={true}
              showTypedErrors={true}
              showDropdownIcon={true}
              withDropDownSearch
              defaultSelected={couponCategoryItem?.filter((oc) => oc.title === formHook.watch("vmscategory"))[0]}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full sm:w-[300px]"
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
            <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
              <span className="font-normal text-black text-xs">Sub-Category</span>
              <span className="font-normal text-rose-600 text-xs">*</span>
            </div>
            <SearchableSelectMenu
              errorMessages={[{ message: "Sub-Category is required", type: "required" }]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("vmsSubcategory", item.title);
                  formHook.setValue("vmsSubcategoryId", item.id);
                }
              }}
              fieldError={formHook.formState.errors.vmsSubcategory}
              register={formHook.register("vmsSubcategory", { required: true })}
              selectItems={couponSubCategoryItem}
              placeholder="Select Sub-Category"
              showTooltips={true}
              showTypedErrors={true}
              showDropdownIcon={true}
              withDropDownSearch
              defaultSelected={couponSubCategoryItem?.filter((oc) => oc.title === formHook.watch("vmsSubcategory"))[0]}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full sm:w-[300px]"
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
            <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
              <span className="font-normal text-black text-xs">Pre-requisities</span>
            </div>
            <Input
              register={formHook?.register("requisities", {
                required: false,
              })}
              type="text"
              fieldError={formHook?.formState?.errors?.requisities}
              errorMessages={[{ message: "", type: "required" }]}
              className="border-slate-400 rounded-md w-full sm:w-[300px] h-9 text-sm"
              inputClassName="h-[38px] rounded-md"
              placeholder="e.g. 3 hours fasting"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                formHook?.setValue("requisities", e.target.value);
                formHook.clearErrors("requisities");
              }}
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
            <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
              <span className="font-normal text-black text-xs">Document Type</span>
            </div>
            <SearchableSelectMenu
              errorMessages={[{ message: "", type: "required" }]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("certificate_type", item.title);
                  formHook.setValue("certificate_id", item.id);
                } else {
                  formHook.setValue("certificate_type", "");
                  formHook.setValue("certificate_id", "");
                }
              }}
              fieldError={formHook.formState.errors.certificate_type}
              register={formHook.register("certificate_type", { required: false })}
              selectItems={certificates}
              placeholder="Select Certificate"
              showTooltips={true}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={certificates?.filter((oc) => oc.title === formHook.watch("certificate_type"))[0]}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full sm:w-[300px]"
              withReset
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
            <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
              <span className="font-normal text-black text-xs">Comments</span>
            </div>
            <textarea
              value={formHook.watch("comments")}
              className="border-gray-300 border rounded-md w-full sm:w-[300px] h-20 text-xs px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-600"
              placeholder="Write any comments, if required"
              onChange={(e) => formHook.setValue("comments", e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
            <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
              <span className="font-normal text-black text-xs">Invoice</span>
            </div>
            <textarea
              value={formHook.watch("invoice")}
              className="border-gray-300 border rounded-md w-full sm:w-[300px] h-20 text-xs px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-600"
              placeholder="Invoice TnC, if required"
              onChange={(e) => formHook.setValue("invoice", e.target.value)}
            />
          </div>
          {isLabBomType && (
            <>
              <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
                <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
                  <span className="font-normal text-black text-xs">Sample Type</span>
                </div>
                <SearchableSelectMenu
                  errorMessages={[]}
                  onSelectItem={(item) => {
                    if (item) {
                      formHook.setValue("sampleType", item.title);
                    }
                  }}
                  fieldError={formHook.formState.errors.sampleType}
                  register={formHook.register("sampleType", { required: false })}
                  selectItems={bomSampleTypeItems}
                  placeholder="Select Sample Type"
                  showTooltips={true}
                  showTypedErrors={true}
                  showDropdownIcon={true}
                  withDropDownSearch
                  defaultSelected={bomSampleTypeItems?.filter((oc) => oc.title === formHook.watch("sampleType"))[0]}
                  listBoxClassName="w-full"
                  className="text-gray-800"
                  containerClassName="w-full sm:w-[300px]"
                />
              </div>
              <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
                <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
                  <span className="font-normal text-black text-xs">Vial Type</span>
                  <span className="font-normal text-rose-600 text-xs">*</span>
                </div>
                <SearchableMultiSelectMenu
                  errorMessages={[{ message: "Vial Type is required", type: "required" }]}
                  onSelectItem={(items) => {
                    formHook.setValue(
                      "vialType",
                      (items ?? []).map((item) => ({ id: item.id, name: item.title })),
                    );
                  }}
                  fieldError={formHook.formState.errors.vialType}
                  register={formHook.register("vialType", { required: isLabBomType && false })}
                  selectItems={bomVialTypeItems}
                  placeholder="Select Vial Type"
                  showTooltips={true}
                  showTypedErrors={true}
                  defaultSelected={bomVialTypeItems?.filter((oc) => {
                    const vialType = formHook.watch("vialType");
                    return Array.isArray(vialType) && vialType.some((vial) => vial?.id === oc.id);
                  })}
                  listBoxClassName="w-full"
                  className="text-gray-800"
                  containerClassName="w-full sm:w-[300px]"
                />
              </div>
              <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
                <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
                  <span className="font-normal text-black text-xs">Sample Quantity</span>
                </div>
                <Input
                  register={formHook?.register("sampleQuantity", {
                    required: false,
                  })}
                  type="text"
                  fieldError={formHook?.formState?.errors?.sampleQuantity}
                  errorMessages={[{ message: "", type: "required" }]}
                  className="border-slate-400 rounded-md w-full sm:w-[300px] h-9 text-sm"
                  inputClassName="h-[38px] rounded-md"
                  placeholder="e.g. 2ml"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    formHook?.setValue("sampleQuantity", e.target.value);
                    formHook.clearErrors("sampleQuantity");
                  }}
                />
              </div>
              <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
                <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
                  <span className="font-normal text-black text-xs">Parameters Included</span>
                </div>
                <SearchableMultiSelectMenu
                  errorMessages={[{ message: "Parameters Included is required", type: "required" }]}
                  onSelectItem={(items) => {
                    formHook.setValue(
                      "parametersIncluded",
                      (items ?? []).map((item) => {
                        const matchedParameter = (bomLisParameters || []).find(
                          (param) => param?.parameter_id === item?.id,
                        );
                        return {
                          id: item?.id,
                          name: matchedParameter?.parameter_name ?? item?.title,
                          method_id: matchedParameter?.method_id ?? null,
                        };
                      }),
                    );
                  }}
                  fieldError={formHook.formState.errors.parametersIncluded}
                  register={formHook.register("parametersIncluded", { required: isLabBomType && false })}
                  selectItems={bomLisParametersItems}
                  placeholder="Select Parameters"
                  showTooltips={true}
                  showTypedErrors={true}
                  defaultSelected={bomLisParametersItems?.filter((oc) => {
                    const parametersIncluded = formHook.watch("parametersIncluded");
                    return Array.isArray(parametersIncluded) && parametersIncluded.some((param) => param?.id === oc.id);
                  })}
                  listBoxClassName="w-full"
                  className="text-gray-800"
                  containerClassName="w-full sm:w-[300px]"
                />
              </div>
            </>
          )}
          {isBomType && (
            <>
              <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
                <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
                  <span className="font-normal text-black text-xs">Family of Vaccine</span>
                  <span className="font-normal text-rose-600 text-xs">*</span>
                </div>
                <SearchableSelectMenu
                  errorMessages={[{ message: "Family of Vaccine is required", type: "required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      formHook.setValue("familyVaccine", item.title);
                      formHook.setValue("familyVaccineId", item.id);
                      formHook.setValue("dose", "");
                      formHook.setValue("doseId", "");
                    }
                  }}
                  fieldError={formHook.formState.errors.familyVaccine}
                  register={formHook.register("familyVaccine", { required: isBomType && false })}
                  selectItems={bomVaccineFamilyDoseItems}
                  placeholder="e.g. ARV"
                  showTooltips={true}
                  showTypedErrors={true}
                  showDropdownIcon={true}
                  withDropDownSearch
                  defaultSelected={
                    bomVaccineFamilyDoseItems?.filter((oc) => oc.title === formHook.watch("familyVaccine"))[0]
                  }
                  listBoxClassName="w-full"
                  className="text-gray-800"
                  containerClassName="w-full sm:w-[300px]"
                />
              </div>
              <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
                <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
                  <span className="font-normal text-black text-xs">Dose</span>
                  <span className="font-normal text-rose-600 text-xs">*</span>
                </div>
                <SearchableSelectMenu
                  errorMessages={[{ message: "Dose is required", type: "required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      formHook.setValue("dose", item.title);
                      formHook.setValue("doseId", item.id);
                    }
                  }}
                  fieldError={formHook.formState.errors.dose}
                  register={formHook.register("dose", { required: isBomType && false })}
                  selectItems={doseItems}
                  placeholder="e.g. 1st ARV"
                  showTooltips={true}
                  showTypedErrors={true}
                  showDropdownIcon={true}
                  withDropDownSearch
                  defaultSelected={doseItems?.filter((oc) => oc.title === formHook.watch("dose"))[0]}
                  listBoxClassName="w-full"
                  className="text-gray-800"
                  containerClassName="w-full sm:w-[300px]"
                />
              </div>
            </>
          )}
          <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
            <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
              <span className="font-normal text-black text-xs">Component Products</span>
              <span className="font-normal text-rose-600 text-xs">*</span>
            </div>
            <InventoryProductSearchComponent
              onSelectItem={(item) => {
                handleAddProductItem(item);
              }}
              containerClassName="shadow-none w-full sm:w-[300px]"
              itemActiveClassName="bg-orange-500 text-white"
              inputContainerClassName="w-full"
              flexibleAdd={false}
              placeholder="Search Products"
            />
          </div>
          {formHook.watch("products")?.length > 0 && (
            <div className="mt-5 overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-[640px] w-full border-separate border-spacing-0 divide-y divide-gray-200">
                <thead className="top-0 sticky z-10 bg-[#f5f5f5]">
                  <tr>
                    <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm text-black font-medium">
                      SKU Name
                    </th>
                    <th scope="col" className="px-3 sm:px-6 py-3 text-center text-xs sm:text-sm text-black font-medium">
                      Quantity / Pack
                    </th>
                    <th scope="col" className="px-3 sm:px-6 py-3 text-center text-xs sm:text-sm text-black font-medium">
                      Qty
                    </th>
                    <th scope="col" className="px-3 sm:px-6 py-3 text-center text-xs sm:text-sm text-black font-medium">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-orange-100">
                  <input type="hidden" {...register("products")} />
                  {formHook.watch("products")?.map((field, index) => {
                    return (
                      <tr key={field?.variant_id}>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs font-normal bg-gray-50">
                          {formHook.getValues(`products.${index}.sku_name`)}
                        </td>
                        <td className="px-3 sm:px-6 font-semibold py-4 whitespace-nowrap text-xs text-center">
                          {formHook.getValues(`products.${index}.qty_per_pkg`)}
                        </td>
                        <td className="px-3 sm:px-6 font-semibold py-4">
                          <div className="flex justify-center">
                            <Input
                              register={formHook?.register(`products.${index}.qty`, {
                                validate: (value) => {
                                  const isNumeric = /^[0-9]*$/.test(value);
                                  return isNumeric || "Only numbers are allowed";
                                },
                                required: true,
                                min: 1,
                              })}
                              type="number"
                              min={1}
                              fieldError={formHook?.formState?.errors?.products?.[index]?.qty}
                              errorMessages={[
                                forOnlyNumbers?.errors,
                                { type: "required", message: "Qty is required" },
                                { type: "min", message: "Product Quantity should be greater than 0" },
                                { type: "validate", message: "Only numbers are allowed" },
                              ]}
                              className="border-slate-400 rounded-md w-full sm:w-[140px] h-9 text-sm"
                              inputClassName="h-[38px] rounded-md"
                              placeholder="Enter Qty"
                            />
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs font-normal text-center">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const prevProducts = formHook.watch("products");
                              formHook.setValue(
                                "products",
                                prevProducts.filter((_, i) => i != index),
                              );
                            }}
                          >
                            <TrashIcon className="w-4 h-4 text-red-500" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
            <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
              <span className="font-normal text-black text-xs">Visible on VMS</span>
            </div>
            <ToggleWithIcon
              onToggleChange={(status) => {
                formHook.setValue("vmsVisible", status);
              }}
              toggleState={formHook.watch("vmsVisible")}
            />
          </div>
          <div className="bottom-0 right-0 sticky flex flex-row justify-end items-center gap-3 sm:gap-x-4 bg-white mt-8 px-2 py-3 border-t-2 w-full">
            <button type="button" className={SECONDARY_BUTTON} onClick={handleCancel}>
              Cancel
            </button>
            <button type="submit" className={PRIMARY_BUTTON}>
              Save & Next
            </button>
          </div>
        </form>
      </RBACFlag>

      <RBACFlag actionId="BOM_TAX_DETAILS">
        <form
          className="bg-white py-4 rounded-md mb-3 w-full"
          onSubmit={async (event) => {
            event.preventDefault();
            const isValid = await formHook.trigger(["hsncode", "cgst", "sgst", "igst", "ugst"] as any);
            if (!isValid) return;
            await handleTaxOnboard(formHook.getValues());
          }}
        >
          <p className="text-md font-semibold">Tax Details</p>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
            <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
              <span className="font-normal text-black text-xs">HSN Code</span>
              <span className="font-normal text-rose-600 text-xs">*</span>
            </div>
            <SearchableSelectMenu
              errorMessages={[{ message: "HSN Code is required", type: "required" }]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("hsncode", item.title);
                  formHook.clearErrors("hsncode");
                }
              }}
              fieldError={formHook.formState.errors.hsncode}
              register={formHook.register("hsncode", { required: true })}
              selectItems={taxListItems}
              placeholder="Select HSN Code"
              showTooltips={true}
              showTypedErrors={true}
              showDropdownIcon={true}
              defaultSelected={taxListItems?.filter((oc) => oc.title === formHook.watch("hsncode"))[0]}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full sm:w-[300px]"
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
            <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
              <span className="font-normal text-black text-xs">CGST</span>
              <span className="font-normal text-rose-600 text-xs">*</span>
            </div>
            <Input
              className="border-slate-400 rounded-md w-full sm:w-[300px] h-9 text-sm"
              inputClassName="h-[38px] rounded-md"
              placeholder="CGST %"
              register={formHook.register("cgst", {
                required: true,
                valueAsNumber: true,
                ...forOnlyNumbersWithDecimal.validations,
                validate: {
                  nonNegative: (value: unknown) => {
                    const parsedValue = parseOptionalNumber(value);
                    return parsedValue === null || parsedValue >= 0;
                  },
                  withinLimit: (value: unknown) => {
                    const parsedValue = parseOptionalNumber(value);
                    return parsedValue === null || parsedValue <= 100;
                  },
                },
              })}
              fieldError={formHook.formState.errors?.cgst}
              errorMessages={[
                { message: "CGST % is required", type: "required" },
                forOnlyNumbersWithDecimal.errors,
                { message: "CGST % cannot be negative", type: "nonNegative" },
                { message: "CGST % cannot be greater than 100", type: "withinLimit" },
              ]}
              type="number"
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
            <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
              <span className="font-normal text-black text-xs">SGST</span>
              <span className="font-normal text-rose-600 text-xs">*</span>
            </div>
            <Input
              className="border-slate-400 rounded-md w-full sm:w-[300px] h-9 text-sm"
              inputClassName="h-[38px] rounded-md"
              placeholder="SGST %"
              register={formHook.register("sgst", {
                required: true,
                valueAsNumber: true,
                ...forOnlyNumbersWithDecimal.validations,
                validate: {
                  nonNegative: (value: unknown) => {
                    const parsedValue = parseOptionalNumber(value);
                    return parsedValue === null || parsedValue >= 0;
                  },
                  withinLimit: (value: unknown) => {
                    const parsedValue = parseOptionalNumber(value);
                    return parsedValue === null || parsedValue <= 100;
                  },
                },
              })}
              fieldError={formHook.formState.errors?.sgst}
              errorMessages={[
                { message: "SGST % is required", type: "required" },
                forOnlyNumbersWithDecimal.errors,
                { message: "SGST % cannot be negative", type: "nonNegative" },
                { message: "SGST % cannot be greater than 100", type: "withinLimit" },
              ]}
              type="number"
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
            <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
              <span className="font-normal text-black text-xs">IGST</span>
              <span className="font-normal text-rose-600 text-xs">*</span>
            </div>
            <Input
              className="border-slate-400 rounded-md w-full sm:w-[300px] h-9 text-sm"
              inputClassName="h-[38px] rounded-md"
              placeholder="IGST %"
              register={formHook.register("igst", {
                required: true,
                valueAsNumber: true,
                ...forOnlyNumbersWithDecimal.validations,
                validate: {
                  nonNegative: (value: unknown) => {
                    const parsedValue = parseOptionalNumber(value);
                    return parsedValue === null || parsedValue >= 0;
                  },
                  withinLimit: (value: unknown) => {
                    const parsedValue = parseOptionalNumber(value);
                    return parsedValue === null || parsedValue <= 100;
                  },
                },
              })}
              fieldError={formHook.formState.errors?.igst}
              errorMessages={[
                { message: "IGST % is required", type: "required" },
                forOnlyNumbersWithDecimal.errors,
                { message: "IGST % cannot be negative", type: "nonNegative" },
                { message: "IGST % cannot be greater than 100", type: "withinLimit" },
              ]}
              type="number"
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
            <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
              <span className="font-normal text-black text-xs">UTGST</span>
              <span className="font-normal text-rose-600 text-xs">*</span>
            </div>
            <Input
              className="border-slate-400 rounded-md w-full sm:w-[300px] h-9 text-sm"
              inputClassName="h-[38px] rounded-md"
              placeholder="UTGST %"
              register={formHook.register("ugst", {
                required: true,
                valueAsNumber: true,
                ...forOnlyNumbersWithDecimal.validations,
                validate: {
                  nonNegative: (value: unknown) => {
                    const parsedValue = parseOptionalNumber(value);
                    return parsedValue === null || parsedValue >= 0;
                  },
                  withinLimit: (value: unknown) => {
                    const parsedValue = parseOptionalNumber(value);
                    return parsedValue === null || parsedValue <= 100;
                  },
                },
              })}
              fieldError={formHook.formState.errors?.ugst}
              errorMessages={[
                { message: "UTGST % is required", type: "required" },
                forOnlyNumbersWithDecimal.errors,
                { message: "UTGST % cannot be negative", type: "nonNegative" },
                { message: "UTGST % cannot be greater than 100", type: "withinLimit" },
              ]}
              type="number"
            />
          </div>
          <div className="bottom-0 right-0 sticky flex flex-row justify-end items-center gap-3 sm:gap-x-4 bg-white mt-8 px-2 py-3 border-t-2 w-full">
            <button type="button" className={SECONDARY_BUTTON} onClick={handleCancel}>
              Cancel
            </button>
            <button type="submit" className={PRIMARY_BUTTON}>
              Save
            </button>
          </div>
        </form>
      </RBACFlag>
    </div>
  );
};

export default BomGeneralDetails;
